import { useEffect, useMemo, useReducer, useRef, useState } from "react"
import type { ApiEstimate, VehicleType } from "@/lib/api/types"
import {
  readIntakeMeta,
  type IntakeBestTime,
  type IntakeCause,
  type IntakeContactChannel,
  type IntakeDrivable,
  type IntakeInsuranceMode,
  type VehicleDecodedFrom,
} from "@/lib/api/public-consumer"
import type { BookedInfo } from "./booking-flow"
import { SURFACE_RULES } from "./surfaces"
import type { Entry, ExtrasStep, IntakeDevice, IntakeSurface, Phase } from "./intake-flow-types"
import { CONSUMER_FLOW_DRAFT_KEY, type DamageZone } from "./types"
import { captureFromEstimate } from "./capture/hydrate"
import {
  EMPTY_CAPTURE,
  type CaptureState,
  type ShotId,
  type ShotRecord,
} from "./capture/types"

// Consumer intake — one reducer for the whole public flow (contact-first).
// The staff wizard's hook (use-consumer-intake-estimate-state.ts) is untouched.
//
// Persistence: the EXISTING key etx_consumer_flow_draft_v1, inner `v` bumped
// to 4 for the damage-led photo walk (older shapes are discarded), 14-day
// TTL, written ONLY when SURFACE_RULES[surface].persistDraft (link/qr/sms).
// Only photos that reached the server are persisted (by URL); blob previews
// and files never touch storage. NO new storage keys.
//
// The reducer is pure: object URLs are owned by uploads/preview-registry.ts.

const DRAFT_V = 4
const DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000

export interface IntakeFlowVehicle {
  vin: string
  plate: string
  year: string
  make: string
  model: string
  trim: string
  type: VehicleType | null
  decodedFrom: VehicleDecodedFrom | null
}

export interface IntakeFlowState {
  v: typeof DRAFT_V
  shopId: string
  surface: IntakeSurface
  device: IntakeDevice
  phase: Phase
  extrasStep: ExtrasStep
  customer: { name: string; phone: string; email: string; smsOptIn: boolean }
  result: {
    estimateId: number
    trackToken: string
    continueUrl: string
    createdAt: number
  } | null
  vehicle: IntakeFlowVehicle
  capture: CaptureState
  situation: { drivable: IntakeDrivable | null; cause: IntakeCause | null; note: string }
  insurance: { mode: IntakeInsuranceMode | null; carrier: string; claimNumber: string }
  contactPref: { channel: IntakeContactChannel | null; bestTime: IntakeBestTime | null }
  booked: BookedInfo | null
  photosLaterSent: boolean
  extrasDoneAt: number | null
  revealSeenAt: number | null
  synced: {
    details: boolean
    // The "Send photos" (final) append reached the server.
    photos: boolean
  }
  updatedAt: number
}

export const EMPTY_VEHICLE: IntakeFlowVehicle = {
  vin: "",
  plate: "",
  year: "",
  make: "",
  model: "",
  trim: "",
  type: null,
  decodedFrom: null,
}

function initialState(shopId: string, surface: IntakeSurface, device: IntakeDevice): IntakeFlowState {
  return {
    v: DRAFT_V,
    shopId,
    surface,
    device,
    phase: "contact",
    extrasStep: "vehicle",
    customer: { name: "", phone: "", email: "", smsOptIn: true },
    result: null,
    vehicle: EMPTY_VEHICLE,
    capture: EMPTY_CAPTURE,
    situation: { drivable: null, cause: null, note: "" },
    insurance: { mode: null, carrier: "", claimNumber: "" },
    contactPref: { channel: null, bestTime: null },
    booked: null,
    photosLaterSent: false,
    extrasDoneAt: null,
    revealSeenAt: null,
    synced: { details: true, photos: true },
    updatedAt: 0,
  }
}

type Action =
  | { type: "set-phase"; phase: Phase }
  | { type: "set-extras-step"; step: ExtrasStep }
  | { type: "set-customer"; customer: Partial<IntakeFlowState["customer"]> }
  | { type: "set-result"; result: IntakeFlowState["result"] }
  | { type: "set-vehicle"; vehicle: Partial<IntakeFlowVehicle> }
  | { type: "set-zones"; zones: DamageZone[]; unsure: boolean }
  | { type: "put-shot"; id: ShotId; record: ShotRecord }
  | { type: "patch-shot"; key: string; patch: Partial<ShotRecord> }
  | { type: "remove-shot"; key: string }
  | { type: "skip-shot"; id: ShotId }
  | { type: "add-extras"; records: ShotRecord[] }
  | { type: "set-voice"; voice: boolean }
  | { type: "mark-photos-done" }
  | { type: "mark-photos-synced"; synced: boolean }
  | { type: "set-situation"; situation: Partial<IntakeFlowState["situation"]> }
  | { type: "set-insurance"; insurance: Partial<IntakeFlowState["insurance"]> }
  | { type: "set-contact-pref"; contactPref: Partial<IntakeFlowState["contactPref"]> }
  | { type: "set-booked"; booked: BookedInfo | null }
  | { type: "mark-photos-later" }
  | { type: "mark-extras-done" }
  | { type: "mark-reveal-seen" }
  | { type: "mark-synced"; synced: boolean }
  | { type: "hydrate"; state: IntakeFlowState }
  | { type: "reset"; state: IntakeFlowState }

function stamp(state: IntakeFlowState): IntakeFlowState {
  return { ...state, updatedAt: Date.now() }
}

function withCapture(state: IntakeFlowState, capture: Partial<CaptureState>): IntakeFlowState {
  return stamp({ ...state, capture: { ...state.capture, ...capture } })
}

// Patch a record wherever it lives (a planned slot or the extras list).
function patchCaptureRecord(
  capture: CaptureState,
  key: string,
  patch: Partial<ShotRecord>,
): CaptureState {
  const slot = capture.shots[key as ShotId]
  if (slot) return { ...capture, shots: { ...capture.shots, [key]: { ...slot, ...patch } } }
  if (capture.extras.some((r) => r.key === key)) {
    return {
      ...capture,
      extras: capture.extras.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    }
  }
  return capture
}

function reducer(state: IntakeFlowState, action: Action): IntakeFlowState {
  switch (action.type) {
    case "set-phase":
      return stamp({ ...state, phase: action.phase })
    case "set-extras-step":
      return stamp({ ...state, extrasStep: action.step })
    case "set-customer":
      return stamp({ ...state, customer: { ...state.customer, ...action.customer } })
    case "set-result":
      return stamp({ ...state, result: action.result })
    case "set-vehicle":
      return stamp({ ...state, vehicle: { ...state.vehicle, ...action.vehicle } })
    case "set-zones":
      return withCapture(state, {
        zones: [...new Set(action.zones)],
        unsure: action.unsure,
        zonesConfirmed: true,
      })
    case "put-shot":
      return withCapture(state, {
        shots: { ...state.capture.shots, [action.id]: action.record },
        skipped: state.capture.skipped.filter((id) => id !== action.id),
      })
    case "patch-shot":
      return stamp({ ...state, capture: patchCaptureRecord(state.capture, action.key, action.patch) })
    case "remove-shot": {
      const shots = { ...state.capture.shots }
      delete shots[action.key as ShotId]
      return withCapture(state, {
        shots,
        extras: state.capture.extras.filter((r) => r.key !== action.key),
      })
    }
    case "skip-shot":
      return state.capture.skipped.includes(action.id)
        ? state
        : withCapture(state, { skipped: [...state.capture.skipped, action.id] })
    case "add-extras":
      return withCapture(state, { extras: [...state.capture.extras, ...action.records] })
    case "set-voice":
      return withCapture(state, { voice: action.voice })
    case "mark-photos-done":
      return stamp({
        ...state,
        capture: { ...state.capture, photosDoneAt: state.capture.photosDoneAt ?? Date.now() },
        synced: { ...state.synced, photos: false },
      })
    case "mark-photos-synced":
      return stamp({ ...state, synced: { ...state.synced, photos: action.synced } })
    case "set-situation":
      return stamp({ ...state, situation: { ...state.situation, ...action.situation } })
    case "set-insurance":
      return stamp({ ...state, insurance: { ...state.insurance, ...action.insurance } })
    case "set-contact-pref":
      return stamp({ ...state, contactPref: { ...state.contactPref, ...action.contactPref } })
    case "set-booked":
      return stamp({ ...state, booked: action.booked })
    case "mark-photos-later":
      return stamp({ ...state, photosLaterSent: true })
    case "mark-extras-done":
      return stamp({ ...state, extrasDoneAt: state.extrasDoneAt ?? Date.now() })
    case "mark-reveal-seen":
      return stamp({ ...state, revealSeenAt: state.revealSeenAt ?? Date.now() })
    case "mark-synced":
      return stamp({ ...state, synced: { ...state.synced, details: action.synced } })
    case "hydrate":
    case "reset":
      return action.state
    default:
      return state
  }
}

/* ── Draft persistence (existing key, inner v: 4) ─────────────────────── */

// Persisted twin of IntakeFlowState — booked.date flattened to an ISO string
// (Dates don't survive JSON); only server-side photos, previews = their URL.
interface PersistedBooked {
  type: BookedInfo["type"]
  dateIso: string
  time: string
}
type PersistedDraft = Omit<IntakeFlowState, "booked"> & {
  booked: PersistedBooked | null
  // Photos that hadn't reached the server when this was written (they can't
  // survive a reload) — so the next visit can say so instead of losing them silently.
  unsentPhotos?: number
}

function persistRecord(r: ShotRecord): ShotRecord | null {
  if (!r.uploadedUrl) return null // nothing recoverable without the server URL
  // A phone check still running when this was written won't come back.
  return { ...r, previewUrl: r.uploadedUrl, phone: r.phone ?? null }
}

export function persistCapture(capture: CaptureState): CaptureState {
  const shots: Partial<Record<ShotId, ShotRecord>> = {}
  for (const [id, r] of Object.entries(capture.shots) as Array<[ShotId, ShotRecord | undefined]>) {
    const kept = r && persistRecord(r)
    if (kept) shots[id] = kept
  }
  return {
    ...capture,
    shots,
    extras: capture.extras.map(persistRecord).filter((r): r is ShotRecord => r !== null),
  }
}

function writeDraft(state: IntakeFlowState) {
  try {
    const capture = persistCapture(state.capture)
    const unsentPhotos =
      Object.keys(state.capture.shots).length +
      state.capture.extras.length -
      (Object.keys(capture.shots).length + capture.extras.length)
    const draft: PersistedDraft = {
      ...state,
      capture,
      unsentPhotos,
      booked: state.booked
        ? {
            type: state.booked.type,
            dateIso: state.booked.date.toISOString(),
            time: state.booked.time,
          }
        : null,
    }
    localStorage.setItem(CONSUMER_FLOW_DRAFT_KEY, JSON.stringify(draft))
  } catch {
    // Quota / disabled storage — the flow still works in-memory.
  }
}

function readDraft(shopId: string): { state: IntakeFlowState; unsentPhotos: number } | null {
  try {
    const raw = localStorage.getItem(CONSUMER_FLOW_DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedDraft
    if (parsed.v !== DRAFT_V || Date.now() - parsed.updatedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(CONSUMER_FLOW_DRAFT_KEY)
      return null
    }
    // Draft for a different shop → leave it alone, start fresh here.
    if (String(parsed.shopId) !== String(shopId)) return null
    const { unsentPhotos = 0, ...rest } = parsed
    return {
      unsentPhotos,
      state: {
        ...rest,
        capture: { ...EMPTY_CAPTURE, ...persistCapture(parsed.capture ?? EMPTY_CAPTURE) },
        booked: parsed.booked
          ? {
              type: parsed.booked.type,
              date: new Date(parsed.booked.dateIso),
              time: parsed.booked.time,
            }
          : null,
      },
    }
  } catch {
    return null
  }
}

export function clearFlowDraft() {
  try {
    localStorage.removeItem(CONSUMER_FLOW_DRAFT_KEY)
  } catch {
    // ignore
  }
}

/* ── Continuation hydration (?c=<token> → estimate → state) ───────────── */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function parseMs(iso: string | null): number | null {
  if (!iso) return null
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : null
}

// Where a continuation lands: done (booked) / offer (reveal seen) / reveal
// (questions answered) / extras (photos sent) / photos (anything else — the
// photo step resumes on Review when photos exist). The reveal phase itself
// waits for the analysis when needed.
export function landingPhaseFor(estimate: ApiEstimate): Phase {
  const meta = readIntakeMeta(estimate)
  const hasAppointment = isRecord(estimate.metadata) && isRecord(estimate.metadata.appointment)
  if (hasAppointment) return "done"
  if (meta?.revealSeenAt) return "offer"
  if (meta?.extrasDoneAt) return "reveal"
  if (meta?.photosDoneAt) return "extras"
  return "photos"
}

function stateFromEstimate(
  base: IntakeFlowState,
  estimate: ApiEstimate,
  trackToken: string,
  shopWantsVehicle: boolean,
): IntakeFlowState {
  const meta = readIntakeMeta(estimate)
  const contact = isRecord(estimate.metadata) && isRecord(estimate.metadata.contact)
    ? estimate.metadata.contact
    : null
  const customerEntity = estimate.vehicle?.customer ?? null
  const name =
    customerEntity?.name ?? (typeof contact?.name === "string" ? contact.name : "")
  const phone =
    customerEntity?.phone ?? (typeof contact?.phone === "string" ? contact.phone : "")
  const email =
    customerEntity?.email ?? (typeof contact?.email === "string" ? contact.email : "")

  // A desktop that opens a continuation link still can't take photos — it
  // lands on the handoff (QR + live mirror) instead.
  const landing = landingPhaseFor(estimate)
  return {
    ...base,
    phase: landing === "photos" && base.device === "desktop" ? "handoff" : landing,
    customer: {
      name,
      phone,
      email: email ?? "",
      smsOptIn: meta?.consent?.smsOptIn ?? true,
    },
    result: {
      estimateId: estimate.id,
      trackToken,
      continueUrl: meta?.handoff?.continueUrl ?? "",
      createdAt: parseMs(meta?.createdAt ?? null) ?? Date.now(),
    },
    vehicle: {
      vin: estimate.vehicle?.vin ?? "",
      plate: meta?.vehicle?.plate ?? "",
      year: estimate.vehicle?.year ?? "",
      make: estimate.vehicle?.make ?? "",
      model: estimate.vehicle?.model ?? "",
      trim: estimate.vehicle?.trim ?? "",
      type: estimate.vehicle?.type ?? null,
      decodedFrom: meta?.vehicle?.decodedFrom ?? null,
    },
    capture: captureFromEstimate(estimate, { shopWantsVehicle }),
    situation: meta?.situation ?? base.situation,
    insurance: meta?.insurance ?? base.insurance,
    contactPref: meta?.contactPref ?? base.contactPref,
    extrasDoneAt: parseMs(meta?.extrasDoneAt ?? null),
    revealSeenAt: parseMs(meta?.revealSeenAt ?? null),
    updatedAt: Date.now(),
  }
}

/* ── The hook ─────────────────────────────────────────────────────────── */

export interface UseIntakeFlowStateArgs {
  shopId: string
  surface: IntakeSurface
  device: IntakeDevice
  entry: Entry
  // The ?c= token when entry is a continuation — becomes result.trackToken.
  continuationToken?: string
  // shop.intake.extras.vehicle: whether the walk starts with the VIN step.
  shopWantsVehicle: boolean
}

export function useIntakeFlowState({
  shopId,
  surface,
  device,
  entry,
  continuationToken,
  shopWantsVehicle,
}: UseIntakeFlowStateArgs) {
  const persistDraft = SURFACE_RULES[surface].persistDraft

  // Once per mount: continuation beats the local draft; a valid same-shop
  // draft resumes; anything else starts fresh. All synchronous — no flash.
  const [start] = useState(() => {
    const base = initialState(shopId, surface, device)
    if (entry.kind === "continuation") {
      return {
        state: stateFromEstimate(base, entry.estimate, continuationToken ?? "", shopWantsVehicle),
        resumed: false,
        unsentPhotos: 0,
      }
    }
    if (persistDraft) {
      const draft = readDraft(shopId)
      if (draft) {
        return { state: { ...draft.state, surface, device }, resumed: true, unsentPhotos: draft.unsentPhotos }
      }
    }
    return { state: base, resumed: false, unsentPhotos: 0 }
  })
  const [state, dispatch] = useReducer(reducer, start.state)

  // True when this session woke up from a persisted draft (drives the
  // "Welcome back, {first}" affordance on contact).
  const resumedRef = useRef(start.resumed)

  // Persist on change — but never overwrite a stored draft with the pristine
  // initial state of a fresh tab.
  const dirtyRef = useRef(false)
  useEffect(() => {
    if (!persistDraft) return
    if (!dirtyRef.current) {
      if (state.updatedAt === 0) return
      dirtyRef.current = true
    }
    writeDraft(state)
  }, [state, persistDraft])

  const actions = useMemo(
    () => ({
      setPhase: (phase: Phase) => dispatch({ type: "set-phase", phase }),
      setExtrasStep: (step: ExtrasStep) => dispatch({ type: "set-extras-step", step }),
      setCustomer: (customer: Partial<IntakeFlowState["customer"]>) =>
        dispatch({ type: "set-customer", customer }),
      setResult: (result: IntakeFlowState["result"]) =>
        dispatch({ type: "set-result", result }),
      setVehicle: (vehicle: Partial<IntakeFlowVehicle>) =>
        dispatch({ type: "set-vehicle", vehicle }),
      setZones: (zones: DamageZone[], unsure: boolean) =>
        dispatch({ type: "set-zones", zones, unsure }),
      putShot: (id: ShotId, record: ShotRecord) => dispatch({ type: "put-shot", id, record }),
      patchShot: (key: string, patch: Partial<ShotRecord>) =>
        dispatch({ type: "patch-shot", key, patch }),
      removeShot: (key: string) => dispatch({ type: "remove-shot", key }),
      skipShot: (id: ShotId) => dispatch({ type: "skip-shot", id }),
      addExtras: (records: ShotRecord[]) => dispatch({ type: "add-extras", records }),
      setVoice: (voice: boolean) => dispatch({ type: "set-voice", voice }),
      markPhotosDone: () => dispatch({ type: "mark-photos-done" }),
      markPhotosSynced: (synced: boolean) => dispatch({ type: "mark-photos-synced", synced }),
      setSituation: (situation: Partial<IntakeFlowState["situation"]>) =>
        dispatch({ type: "set-situation", situation }),
      setInsurance: (insurance: Partial<IntakeFlowState["insurance"]>) =>
        dispatch({ type: "set-insurance", insurance }),
      setContactPref: (contactPref: Partial<IntakeFlowState["contactPref"]>) =>
        dispatch({ type: "set-contact-pref", contactPref }),
      setBooked: (booked: BookedInfo | null) => dispatch({ type: "set-booked", booked }),
      markPhotosLater: () => dispatch({ type: "mark-photos-later" }),
      markExtrasDone: () => dispatch({ type: "mark-extras-done" }),
      markRevealSeen: () => dispatch({ type: "mark-reveal-seen" }),
      markSynced: (synced: boolean) => dispatch({ type: "mark-synced", synced }),
      // Desktop "answer the last questions here": adopt the server's state
      // (what the phone did) without losing this device's surface/device.
      hydrateFromEstimate: (estimate: ApiEstimate, trackToken: string) =>
        dispatch({
          type: "hydrate",
          state: stateFromEstimate(
            initialState(shopId, surface, device),
            estimate,
            trackToken,
            shopWantsVehicle,
          ),
        }),
      // Full reset: drop the stored draft, back to an empty contact screen.
      reset: () => {
        dispatch({ type: "reset", state: initialState(shopId, surface, device) })
        resumedRef.current = false
        dirtyRef.current = false
        clearFlowDraft()
      },
    }),
    [shopId, surface, device, shopWantsVehicle],
  )

  return { state, actions, resumed: resumedRef.current, unsentPhotos: start.unsentPhotos }
}

export type IntakeFlowActions = ReturnType<typeof useIntakeFlowState>["actions"]
